import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';

import { bolt11ToSats, classifyZapReceipt } from '@/lib/zaps';
import {
  hotScore,
  isValidStashItem,
  TERRITORIES,
  POST_POW,
  COMMENT_POW,
  REACTION_POW,
} from '@/lib/stashstr';
import { verifyPow } from '@/lib/pow';

export type FeedSort = 'hot' | 'new' | 'top';
export type TopTimeframe = 'day' | 'week' | 'month' | 'year' | 'forever';
export type TopMetric = 'zaps' | 'upvotes' | 'comments';

export const TOP_TIMEFRAMES: TopTimeframe[] = ['day', 'week', 'month', 'year', 'forever'];
export const TOP_METRICS: TopMetric[] = ['zaps', 'upvotes', 'comments'];

export function timeframeToSince(tf: TopTimeframe): number | undefined {
  if (tf === 'forever') return undefined;
  const days: Record<Exclude<TopTimeframe, 'forever'>, number> = {
    day: 1,
    week: 7,
    month: 30,
    year: 365,
  };
  return Math.floor(Date.now() / 1000) - days[tf] * 86400;
}

/** Normalizes path segments; invalid values fall back to defaults. */
export function parseTopParams(
  timeframe?: string,
  metric?: string,
): { timeframe: TopTimeframe; metric: TopMetric } {
  const tf = TOP_TIMEFRAMES.includes(timeframe as TopTimeframe)
    ? (timeframe as TopTimeframe)
    : 'day';
  const m = TOP_METRICS.includes(metric as TopMetric) ? (metric as TopMetric) : 'zaps';
  return { timeframe: tf, metric: m };
}

export interface FeedEntry {
  event: NostrEvent;
  powValid: boolean;
  zapTotal: number;
  zapCount: number;
  /** Legacy receipts with no `p` tag — display-only, never ranked. */
  zapUnverifiedTotal: number;
  zapUnverifiedCount: number;
  commentCount: number;
  /** Up-only count (Top "upvotes" metric ranks by this). */
  reactionCount: number;
  downCount: number;
  /** Net score: reactionCount - downCount. Display-only. */
  score: number;
  author?: NostrMetadata;
}

export function useStashstrFeed(opts: {
  territory?: string;
  sort: FeedSort;
  limit: number;
  since?: number;
  topMetric?: TopMetric;
}) {
  const { nostr } = useNostr();
  const { territory, sort, limit, since, topMetric = 'zaps' } = opts;

  const query = useQuery({
    queryKey: ['stashstr', 'feed', territory ?? 'all', sort, limit, since ?? 'all', topMetric],
    queryFn: async (c) => {
      const signal = c.signal;

      // 1. Fetch posts (limit + headroom so "More" actually pages)
      const posts = await nostr.query(
        [
          {
            kinds: [11],
            ...(territory ? { '#t': [territory] } : { '#t': [...TERRITORIES] }),
            ...(since ? { since } : {}),
            limit: Math.min(100, limit + 20),
          },
        ],
        { signal },
      );

      const items = posts.filter(isValidStashItem);
      if (items.length === 0) {
        return { entries: [], hiddenUnprovenCount: 0 };
      }

      const ids = items.map((e) => e.id);
      const pubkeys = [...new Set(items.map((e) => e.pubkey))];
      const authorById = new Map(items.map((e) => [e.id, e.pubkey]));

      // 2. Enrich with zaps, reactions, comments in ONE round-trip (plus legacy fallbacks)
      // NOTE: relays index lowercase tags only; uppercase filters (#E) are
      // best-effort for NIP-22 compat. The lowercase #e filters carry the load.
      const [enrichment, legacyEnrichment, authors] = await Promise.all([
        nostr.query(
          [
            { kinds: [9735, 7], '#E': ids, limit: 200 },
            { kinds: [1111], '#E': ids, limit: 200 },
          ],
          { signal },
        ),
        nostr.query(
          [
            { kinds: [9735, 7], '#e': ids, limit: 200 },
            { kinds: [1111], '#e': ids, limit: 200 },
          ],
          { signal },
        ),
        nostr.query([{ kinds: [0], authors: pubkeys, limit: pubkeys.length }], { signal }),
      ]);

      const all = [...enrichment, ...legacyEnrichment];

      const zapTotals = new Map<string, number>();
      const zapCounts = new Map<string, number>();
      const zapUnverifiedTotals = new Map<string, number>();
      const zapUnverifiedCounts = new Map<string, number>();
      const commentCounts = new Map<string, number>();
      const reactionCounts = new Map<string, number>();
      const downCounts = new Map<string, number>();

      for (const ev of all) {
        if (ev.kind === 9735) {
          const bolt11 = ev.tags.find(([t]) => t === 'bolt11')?.[1];
          const sats = bolt11 ? bolt11ToSats(bolt11) : 0;
          const targets = ev.tags
            .filter(([t]) => t === 'e' || t === 'E')
            .map(([, id]) => id);
          for (const id of targets) {
            if (!ids.includes(id)) continue;
            const verdict = classifyZapReceipt(ev.tags, authorById.get(id));
            if (verdict === 'spoof') continue;
            if (verdict === 'verified') {
              zapTotals.set(id, (zapTotals.get(id) ?? 0) + sats);
              zapCounts.set(id, (zapCounts.get(id) ?? 0) + 1);
            } else {
              // unverified: missing p tag (legacy receipt) — display-only
              zapUnverifiedTotals.set(id, (zapUnverifiedTotals.get(id) ?? 0) + sats);
              zapUnverifiedCounts.set(id, (zapUnverifiedCounts.get(id) ?? 0) + 1);
            }
          }
        } else if (ev.kind === 7) {
          // Count proven '+' (up) and '-' (down) reactions scoped to posts.
          // NOTE: Top "upvotes" metric ranks by UP-ONLY reactionCount — net
          // score is display-only and never used for ranking.
          if ((ev.content !== '+' && ev.content !== '-') || !verifyPow(ev, REACTION_POW)) continue;
          const k = ev.tags.find(([t]) => t === 'K' || t === 'k')?.[1];
          // Legacy tagless reactions default to post scope; reject only
          // explicit comment votes (K=1111).
          if (k !== undefined && k !== '11') continue;
          const targets = ev.tags.filter(([t]) => t === 'e' || t === 'E').map(([, id]) => id);
          for (const id of targets) {
            if (!ids.includes(id)) continue;
            if (ev.content === '+') {
              reactionCounts.set(id, (reactionCounts.get(id) ?? 0) + 1);
            } else {
              downCounts.set(id, (downCounts.get(id) ?? 0) + 1);
            }
          }
        } else if (ev.kind === 1111) {
          // Only count PoW-proven comments (matches item-page visibility)
          if (!verifyPow(ev, COMMENT_POW)) continue;
          // comments reference the root via E (or legacy e) tag
          const roots = ev.tags
            .filter(([t]) => t === 'E' || t === 'e')
            .map(([, id]) => id);
          for (const id of roots) {
            if (!ids.includes(id)) continue;
            commentCounts.set(id, (commentCounts.get(id) ?? 0) + 1);
          }
        }
      }

      const metadataMap = new Map<string, NostrMetadata>();
      for (const ev of authors) {
        if (ev.kind !== 0) continue;
        try {
          metadataMap.set(ev.pubkey, JSON.parse(ev.content) as NostrMetadata);
        } catch {
          // ignore malformed metadata
        }
      }

      const entries: FeedEntry[] = items.map((event) => {
        const ups = reactionCounts.get(event.id) ?? 0;
        const downs = downCounts.get(event.id) ?? 0;
        return {
          event,
          powValid: verifyPow(event, POST_POW),
          zapTotal: zapTotals.get(event.id) ?? 0,
          zapCount: zapCounts.get(event.id) ?? 0,
          zapUnverifiedTotal: zapUnverifiedTotals.get(event.id) ?? 0,
          zapUnverifiedCount: zapUnverifiedCounts.get(event.id) ?? 0,
          commentCount: commentCounts.get(event.id) ?? 0,
          reactionCount: ups,
          downCount: downs,
          score: ups - downs,
          author: metadataMap.get(event.pubkey),
        };
      });

      // Partition: proven first, unproven after
      const proven = entries.filter((e) => e.powValid);
      const unproven = entries.filter((e) => !e.powValid);

      const sortFn = (a: FeedEntry, b: FeedEntry): number => {
        switch (sort) {
          case 'new':
            return b.event.created_at - a.event.created_at;
          case 'top': {
            const val = (e: FeedEntry): number => {
              switch (topMetric) {
                case 'upvotes':
                  return e.reactionCount;
                case 'comments':
                  return e.commentCount;
                case 'zaps':
                default:
                  return e.zapTotal;
              }
            };
            return val(b) - val(a);
          }
          case 'hot':
          default:
            return (
              hotScore(
                b.zapTotal,
                b.commentCount,
                b.reactionCount,
                b.event.created_at,
              ) -
              hotScore(a.zapTotal, a.commentCount, a.reactionCount, a.event.created_at)
            );
        }
      };

      proven.sort(sortFn);
      unproven.sort(sortFn);

      return {
        entries: [...proven, ...unproven],
        hiddenUnprovenCount: unproven.length,
      };
    },
    staleTime: 30_000,
  });

  return query;
}
